import { useCallback, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Chip,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import { market_pay, residential_verify_pay } from "../../../api/Payment";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { get_page } from "../../../api/PageApi";
import { useSettings } from "../../../context/Settings";


const TOTAL_AMOUNT = 499;

export default function ResidentialDocPayDialog({ open, onClose, totalPayableAmount, property_id,property_name,property_location }) {
  const { id } = useParams();
  const { settings } = useSettings();
  const settingsData = settings?.data ?? null;

  const baseAmount = Number(settingsData?.residential_document_amount || 0);
  const serviceChargePercent = parseFloat(parseFloat(settingsData?.residential_service_charge || 0).toFixed(2));
  const igstPercent = parseFloat(parseFloat(settingsData?.residential_igst || 0).toFixed(2));

  const serviceChargeAmount = parseFloat((baseAmount * (serviceChargePercent / 100)).toFixed(2));
  const igstAmount = parseFloat((baseAmount * (igstPercent / 100)).toFixed(2));
  const totalAmount = parseFloat((baseAmount + serviceChargeAmount + igstAmount).toFixed(2));

  const [plotDetails, setPlotDetails] = useState([]);
  const [agree, setAgree] = useState(false);
  const [terms, setTerms] = useState("");
  const [villasTerms, setVillasTerms] = useState("");
  const [loadingTerms, setLoadingTerms] = useState(false);


  useEffect(() => {
    if (open) {
      const fetchTerms = async () => {
        setLoadingTerms(true);
        try {
          const res = await get_page(
            "gio_rental_yield_document_terms_and_condition"
          );
          if (res?.success && res?.data?.content) {
            setTerms(res.data.content);
          }
          const villasRes = await get_page(
            "flatsvillas_payment_verification_terms_and_condition"
          );
          if (villasRes?.success && villasRes?.data?.content) {
            setVillasTerms(villasRes.data.content);
          }
        } catch (err) {

        } finally {
          setLoadingTerms(false);
        }
      };
      fetchTerms();
    }
  }, [open]);



  const handlePayment = useCallback(async () => {

    if (!agree) {
      toast.error("Agree to terms and Conditions")
      return
    }

    const loadRazorpay = () => {
      return new Promise((res) => {
        if (window.Razorpay) return res(window.Razorpay);
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => res(window.Razorpay);
        script.onerror = () => res(null);
        document.body.appendChild(script);
      });
    };

    const Razorpay = await loadRazorpay();
    if (!Razorpay) {
      toast.error("Razorpay SDK not loaded");
      return;
    }

    const options = {
      key: settingsData?.payment_api_key || process.env.REACT_APP_RAZOR_PAY,
      amount: Math.round(totalAmount * 100),
      currency: "INR",
      name: "Cashback Farms",
      description: "Cashback",
      image: "/assets/icons/__Fav Icon.png",

      handler: async function (response) {
        const transactionId = response.razorpay_payment_id;

        const payLoad = {
          status: "success",
          payment_id: transactionId,
          transaction_details: `Razorpay Payment - ${transactionId}`,
          amount: totalAmount,
          property_id,
        };

        try {
          const res = await residential_verify_pay(payLoad);

          toast.success(
            res?.message || "Your plot verification completed successfully!"
          );

          
          onClose();
          window.location.reload()
        } catch (err) {
          toast.error("Something went wrong!");
        }
      },
      theme: { color: "#C7DD94" },
    };

    const rzpay = new Razorpay(options);
    rzpay.open();
  }, [agree, totalAmount, property_id, onClose, settingsData]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogContent>

        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <VerifiedIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Verify Your Plot
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Verify your plot to enhance visibility and trust
        </Typography>


        <Box sx={{ backgroundColor: "#EEF4FF", borderRadius: 2, p: 2, mb: 2 }}>
          <Typography fontWeight={600}>{property_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {property_location}
          </Typography>
        </Box>


        <Stack spacing={1.2} mb={2}>
          {[
            "Enhanced visibility in search",
            "Trust badge on listing",
            "Priority customer support",
            "Increased buyer confidence",
          ].map((item) => (
            <Stack direction="row" spacing={1} alignItems="center" key={item}>
              <CheckCircleIcon sx={{ color: "#22C55E", fontSize: 18 }} />
              <Typography variant="body2">{item}</Typography>
            </Stack>
          ))}
        </Stack>


        <Stack spacing={1} mb={2} sx={{ borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", py: 1.5 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Verification Fee</Typography>
            <Typography variant="body2" fontWeight={500}>₹{baseAmount}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Service Charge ({serviceChargePercent}%)</Typography>
            <Typography variant="body2" fontWeight={500}>+ ₹{serviceChargeAmount}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">IGST ({igstPercent}%)</Typography>
            <Typography variant="body2" fontWeight={500}>+ ₹{igstAmount}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography fontWeight={600}>Total</Typography>
            <Chip label={`₹${totalAmount}`} color="success" variant="outlined" sx={{ fontWeight: 600 }} />
          </Stack>
        </Stack>


        {terms && (
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 1.5,
              mb: 2,
              backgroundColor: "#f9f9f9",
              fontSize: "0.75rem",
              "& p": { mb: 1 },
              "& ul, & ol": { pl: 2, mb: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: terms }}
          />
        )}

        {villasTerms && (
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 1.5,
              mb: 2,
              backgroundColor: "#f9f9f9",
              fontSize: "0.75rem",
              "& p": { mb: 1 },
              "& ul, & ol": { pl: 2, mb: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: villasTerms }}
          />
        )}

        {/* 
        <FormControlLabel
          control={<Checkbox checked={agree}
              onChange={(e) => setAgree(e.target.checked)}/>}
          label={
            <Typography variant="body2">
              I agree to the{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                <Link to={"/pages/market-place-dashboard"} style={{
                  textDecoration: "none",
                  color: "#92AF5D",
                  fontWeight: "bold",
                }}>Terms and Conditions</Link>
              </Box>
            </Typography>
          }
          sx={{ mb: 2 }}
        /> */}

        <FormControlLabel
          control={
            <Checkbox
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{" "}
              <Link
                to="/pages/flatsvillas_payment_verification_terms_and_condition"
                style={{
                  textDecoration: "none",
                  color: "#92AF5D",
                  fontWeight: "bold",
                }}
                onClick={(e) => e.stopPropagation()} // important!
              >
                Terms and Conditions
              </Link>
            </Typography>
          }
        />



        <Stack direction="row" spacing={2}>
          <Button variant="text" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            sx={{ borderRadius: 6, py: 1.2 }}
            onClick={handlePayment}
          >
            Proceed to Pay
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}


